/**
 * Uptime Bar Component - Balkendiagramm für Uptime-Visualisierung
 * Design inspiriert von status.claude.com
 */
import React, { useState, useEffect } from 'react';
import { getEndpointUptime } from '../../services/api';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

function UptimeBar({ endpointId, days = 90 }) {
  const [uptimeData, setUptimeData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUptimeData();

    // Auto-refresh alle 60 Sekunden, um aktuelle Tagesdaten zu laden
    const interval = setInterval(loadUptimeData, 60000);
    return () => clearInterval(interval);
  }, [endpointId, days]);

  const loadUptimeData = async () => {
    try {
      const response = await getEndpointUptime(endpointId, days);

      // Fülle fehlende Tage mit Platzhaltern auf
      const filledData = fillMissingDays(response.data, days);
      setUptimeData(filledData);
    } catch (error) {
      console.error('Fehler beim Laden der Uptime-Daten:', error);
      // Erstelle Platzhalter-Daten
      setUptimeData(createPlaceholderDays(days));
    } finally {
      setLoading(false);
    }
  };

  const fillMissingDays = (data, totalDays) => {
    // Erstelle Array mit allen Tagen (heute zurück bis totalDays)
    const result = [];
    const today = new Date();

    for (let i = totalDays - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      // Suche nach vorhandenen Daten für diesen Tag
      const existingData = data.find(d => d.stat_date === dateStr);

      if (existingData) {
        result.push(existingData);
      } else {
        // Platzhalter für Tag ohne Daten
        result.push({
          stat_date: dateStr,
          uptime_percentage: null,
          total_checks: 0,
          successful_checks: 0,
          failed_checks: 0,
          total_downtime_seconds: 0,
          avg_response_time: null,
        });
      }
    }

    return result;
  };

  const createPlaceholderDays = (totalDays) => {
    const result = [];
    const today = new Date();

    for (let i = totalDays - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      result.push({
        stat_date: date.toISOString().split('T')[0],
        uptime_percentage: null,
        total_checks: 0,
        successful_checks: 0,
        failed_checks: 0,
        total_downtime_seconds: 0,
      });
    }

    return result;
  };

  const getColorForUptime = (percentage) => {
    if (percentage === null) return '#e9ecef'; // Keine Daten (Grau)
    if (percentage === 100) return '#28a745'; // Perfekt (Grün)
    if (percentage >= 99) return '#9cbf2b'; // Sehr gut (PCS Grün)
    if (percentage >= 95) return '#ffc107'; // Gut (Gelb)
    if (percentage >= 85) return '#fd7e14'; // Akzeptabel (Orange)
    return '#dc3545'; // Kritisch (Rot)
  };

  const formatDowntime = (seconds) => {
    if (!seconds || seconds === 0) return 'Keine Ausfallzeit';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m Ausfallzeit`;
    }
    return `${minutes}m Ausfallzeit`;
  };

  const renderTooltip = (stat) => {
    const date = new Date(stat.stat_date);
    const formattedDate = date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });

    if (stat.uptime_percentage === null) {
      return (
        <Tooltip>
          <strong>{formattedDate}</strong>
          <br />
          <span className="text-muted">Noch keine Daten</span>
        </Tooltip>
      );
    }

    return (
      <Tooltip>
        <strong>{formattedDate}</strong>
        <br />
        <span style={{ color: getColorForUptime(stat.uptime_percentage), fontWeight: 'bold' }}>
          {stat.uptime_percentage.toFixed(2)}% Uptime
        </span>
        <br />
        <small>
          {formatDowntime(stat.total_downtime_seconds)}
          <br />
          {stat.successful_checks} / {stat.total_checks} Checks erfolgreich
          {stat.avg_response_time && (
            <>
              <br />
              Ø {Math.round(stat.avg_response_time)}ms Response
            </>
          )}
        </small>
      </Tooltip>
    );
  };

  if (loading) {
    return (
      <div className="uptime-bar-container">
        <div className="uptime-bar loading">
          <div className="text-center py-2">
            <small className="text-muted">Lädt Verfügbarkeit...</small>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="uptime-bar-container">
      <div className="uptime-bar">
        {uptimeData.map((stat, index) => (
          <OverlayTrigger
            key={`${stat.stat_date}-${index}`}
            placement="top"
            overlay={renderTooltip(stat)}
            delay={{ show: 100, hide: 0 }}
          >
            <div
              className="uptime-day"
              style={{
                backgroundColor: getColorForUptime(stat.uptime_percentage),
              }}
            />
          </OverlayTrigger>
        ))}
      </div>
      <div className="uptime-bar-legend">
        <small className="text-muted">
          <span style={{ marginRight: '15px' }}>
            {days} Tage
          </span>
          <span style={{ marginRight: '8px' }}>
            <span style={{
              display: 'inline-block',
              width: '10px',
              height: '10px',
              backgroundColor: '#28a745',
              marginRight: '4px',
              borderRadius: '2px'
            }}></span>
            100%
          </span>
          <span style={{ marginRight: '8px' }}>
            <span style={{
              display: 'inline-block',
              width: '10px',
              height: '10px',
              backgroundColor: '#9cbf2b',
              marginRight: '4px',
              borderRadius: '2px'
            }}></span>
            &gt;99%
          </span>
          <span style={{ marginRight: '8px' }}>
            <span style={{
              display: 'inline-block',
              width: '10px',
              height: '10px',
              backgroundColor: '#ffc107',
              marginRight: '4px',
              borderRadius: '2px'
            }}></span>
            &gt;95%
          </span>
          <span style={{ marginRight: '8px' }}>
            <span style={{
              display: 'inline-block',
              width: '10px',
              height: '10px',
              backgroundColor: '#fd7e14',
              marginRight: '4px',
              borderRadius: '2px'
            }}></span>
            &gt;85%
          </span>
          <span>
            <span style={{
              display: 'inline-block',
              width: '10px',
              height: '10px',
              backgroundColor: '#dc3545',
              marginRight: '4px',
              borderRadius: '2px'
            }}></span>
            &lt;85%
          </span>
        </small>
      </div>
    </div>
  );
}

export default UptimeBar;
